import { useNavigate } from "react-router-dom";
import { CheckCircle2, X, Sparkles, ArrowRight, Activity, Users, BookOpen, LayoutDashboard } from "lucide-react";

export const ACTIVATION_MODAL = {
  modal: "activation-complete-modal",
  primaryCta: "activation-primary-cta",
  secondaryCta: "activation-secondary-cta",
};

const ACHIEVEMENTS = [
  { label: "Children Added",          Icon: Users },
  { label: "Family Health Score Active", Icon: Activity },
  { label: "Weekly Briefings Enabled",   Icon: BookOpen },
  { label: "Dashboard Ready",            Icon: LayoutDashboard },
];

export default function ActivationCompleteModal({ open, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;

  const goDashboard = () => { onClose(); navigate("/app"); };
  const goHealth = () => { onClose(); /* dashboard scrolls to health card */ navigate("/app"); };

  return (
    <div
      data-testid={ACTIVATION_MODAL.modal}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(22,39,31,0.6)] backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="fcc-card w-full max-w-2xl overflow-hidden relative animate-in fade-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[var(--fcc-cream-2)] flex items-center justify-center text-[var(--fcc-muted)]"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-8 sm:p-10">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-2xl bg-[var(--fcc-green-soft)] flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-[var(--fcc-green)]" />
            </span>
            <span className="fcc-pill fcc-chip-emma">Activation complete</span>
          </div>

          <h2 className="font-display text-4xl sm:text-5xl leading-tight text-[var(--fcc-ink)] mt-6">
            🎉 You&apos;re All Set
          </h2>
          <p className="text-lg text-[var(--fcc-ink-soft)] mt-3">
            Your Family Command Center is ready.
          </p>

          <p className="text-sm text-[var(--fcc-ink-soft)] mt-5 leading-relaxed">
            KidHQ is now actively helping you organize school, sports, activities, forms, deadlines, and family logistics.
            You&apos;re ready to start receiving personalized family insights and weekly briefings.
          </p>

          <ul className="mt-7 grid sm:grid-cols-2 gap-3">
            {ACHIEVEMENTS.map(({ label, Icon }) => (
              <li
                key={label}
                className="flex items-center gap-3 border border-[var(--fcc-border)] rounded-2xl px-4 py-3 bg-[var(--fcc-green-soft)]"
              >
                <CheckCircle2 className="w-5 h-5 text-[var(--fcc-green)] shrink-0" />
                <Icon className="w-4 h-4 text-[var(--fcc-ink-soft)] shrink-0" />
                <span className="text-sm font-semibold text-[var(--fcc-ink)]">{label}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            <button
              data-testid={ACTIVATION_MODAL.primaryCta}
              onClick={goDashboard}
              className="fcc-btn-primary justify-center"
            >
              <LayoutDashboard className="w-4 h-4" /> Open My Dashboard <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid={ACTIVATION_MODAL.secondaryCta}
              onClick={goHealth}
              className="fcc-btn-outline justify-center"
            >
              <Activity className="w-4 h-4" /> View My Family Health Score
            </button>
          </div>

          <div className="mt-7 pt-5 border-t border-[var(--fcc-border)] text-center text-[11px] text-[var(--fcc-muted)] leading-relaxed">
            Powered by All IT Solutions LLC · Smart people, smart solutions
          </div>
        </div>
      </div>
    </div>
  );
}
